/* eslint-disable react/require-default-props */
import React, {useEffect, useRef} from 'react';
import {WithTranslationProps} from 'react-i18next';
import {AccessibilityInfo, findNodeHandle, View} from 'react-native';

import Icon from '/component/base/Icon';
import Text from '/component/base/Text';
import {Reports} from '/constant/TestIDs';
import {withTranslation} from '/provider/TranslationProvider';
import {getTextFromReactNode} from '/util/accessibility.util';

import {styles} from './ReportEmptyState.style';

interface ReportEmptyStateProps extends WithTranslationProps {
  customTitle?: string;
  customSubtitle?: string;
}

export const ReportEmptyState: React.FC<ReportEmptyStateProps> = ({
  tt,
  customTitle,
  customSubtitle,
}) => {
  const containerRef = useRef<View>(null);
  const announcedRef = useRef(false);

  const rawTitle = customTitle || tt('title', 'Sorry, empty');
  const rawSubtitle =
    customSubtitle || tt('subtitle', 'Looks like this report is empty');

  const titleText = getTextFromReactNode(rawTitle) || 'Sorry, empty';
  const subtitleText =
    getTextFromReactNode(rawSubtitle) || 'Looks like this report is empty';
  const message = `${titleText}. ${subtitleText}`;

  useEffect(() => {
    if (announcedRef.current) return;

    const announceEmptyState = async () => {
      if (announcedRef.current) return;

      const screenReaderEnabled =
        await AccessibilityInfo.isScreenReaderEnabled();
      if (!screenReaderEnabled) return;

      announcedRef.current = true;

      const tag = findNodeHandle(containerRef.current);
      if (tag) {
        requestAnimationFrame(() =>
          AccessibilityInfo.setAccessibilityFocus(tag),
        );
      } else {
        AccessibilityInfo.announceForAccessibility(message);
      }
    };

    const timeout = setTimeout(() => {
      announceEmptyState().catch(() => {});
    }, 400);

    return () => clearTimeout(timeout);
  }, [message, tt]);

  return (
    <View
      ref={containerRef}
      style={styles.base}
      accessible
      focusable
      accessibilityRole="text"
      accessibilityLabel={message}>
      <Icon name="info" style={styles.emptyIcon} accessible={false} />
      <Text
        emphasis="high"
        variant="xLarge"
        testID={Reports.reportEmptyMessageTitle}
        style={styles.emptyTitle}>
        {rawTitle}
      </Text>
      <Text
        emphasis="high"
        variant="small"
        testID={Reports.reportEmptyMessageBody}
        style={styles.emptyDescription}>
        {rawSubtitle}
      </Text>
    </View>
  );
};

export default withTranslation('report.reportEmptyState')(ReportEmptyState);
